'use client';
import { template4PlaceholderData, TemplateProps } from '@zeus/shared';
import useFont from '../../../hooks/useFont';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Menu from './components/Menu';
import { getImageSrc, getScaleSize } from '../../../utils/utils';
import Header from './components/Header';
import Heading from './components/Header/Heading';
import Logo from './components/Header/Logo';
import { useNormalizedPixels } from '../../../hooks/useResize';
import defaultImg from '../assets/imgs/Template4bg.png';
import Media from '../Media';
import { extractColors } from 'extract-colors';
import { BoxComponent } from '../../../base/box/Box';
import { TypographyComponent } from '../../../base/typography/Typography';

export const Template4 = forwardRef<HTMLDivElement, TemplateProps>((props, ref) => {
  const { template, scale = 1, style, development, timezone, active, shouldLoad = true } = props;

  const [bgColor, setBgColor] = useState<string>('');

  const mainRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });
  const { normalizePixels } = useNormalizedPixels();

  const getColors = useCallback(async () => {
    let data = null;
    if (defaultImg && !(template.background?.isEnabled && template.background?.image) && !template.background?.color) {
      data = await extractColors(getImageSrc(defaultImg));
      setBgColor(data && data[0].hex);
      return;
    }

    if (template.background?.color && !(template.background?.isEnabled && template.background?.image)) {
      setBgColor(template.background.color);
      return;
    }
  }, [template.background.image, template.background?.isEnabled, template.background?.color]);

  useFont(template?.fontFamily);

  useEffect(() => {
    if (mainRef.current) {
      sizeRef.current = {
        width: mainRef.current.offsetWidth,
        height: mainRef.current.offsetHeight,
      };
      mainRef.current.style.backgroundSize = `${sizeRef.current.width}px ${sizeRef.current.height}px`;
    }
    getColors();
  }, [style?.width, style?.height, getColors]);

  const bgStyles = () => {
    if (template?.background?.isEnabled && template?.background?.image) {
      return { backgroundImage: `url(${template?.background?.image})` };
    }

    if (template?.background?.color) {
      return { backgroundColor: template?.background?.color };
    }

    return {
      backgroundImage: `url(${defaultImg})`,
    };
  };

  const { content, ...rest } = template;
  const menu =
    Array.isArray(template?.content) && template?.content?.length > 0 ? template?.content[0] : template4PlaceholderData;
  const memoizedTemplate = useMemo(() => template, [template]);
  const showHeader = useMemo(
    () => template?.heading?.isEnabled || (template?.brandLogo?.isEnabled && template?.brandLogo?.image),
    [template?.heading, template?.brandLogo]
  );

  const media = template?.media?.mediaContent;
  const isMedia = template.media?.isEnabled && media?.url;
  const isLayoutOne = isMedia && template.layoutType === 1;

  return (
    <div
      ref={mainRef}
      style={{
        ...bgStyles(),
        overflow: 'hidden',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        padding: isLayoutOne
          ? '0px'
          : `${getScaleSize(normalizePixels(20, 'height'), scale)} 
             ${getScaleSize(normalizePixels(51, 'width'), scale)}`,
        ...(!style ? { height: '100vw', width: '100vh' } : style),
      }}
    >
      {/* Heading and logo */}
      {showHeader && (
        <Header scale={scale} isLayoutOne={isLayoutOne as boolean}>
          <Heading template={memoizedTemplate} scale={scale} />
          <Logo template={memoizedTemplate} scale={scale} />
        </Header>
      )}

      <div
        ref={ref}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'column',
          width: '100%',
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          alignContent: 'space-between',
          columnGap: '10%',
          rowGap: getScaleSize(normalizePixels(20, 'height'), scale),
          padding: isLayoutOne
            ? `${getScaleSize(normalizePixels(34, 'height'), scale)} 
              ${getScaleSize(normalizePixels(51, 'width'), scale)}
              ${getScaleSize(normalizePixels(46, 'height'), scale)} `
            : `0px 0px ${getScaleSize(normalizePixels(32, 'height'), scale)}`,
        }}
      >
        <Menu menu={menu} template={rest} development={development} timezone={timezone} scale={scale} />
        {isMedia && template.layoutType === 2 && (
          <div
            id="template-4-layout-2-media"
            style={{
              width: '45%',
              position: 'absolute',
              bottom: '0',
              right: '0%',
              backgroundColor: bgColor,
              height: getScaleSize(normalizePixels(500, 'height'), scale),
            }}
          >
            <Media
              media={media}
              height={getScaleSize(normalizePixels(500, 'height'), scale)}
              playing={active}
              shouldLoad={shouldLoad}
              isDevelopment={development}
            />
          </div>
        )}
        {template.taxLabel.isEnabled && (
          <BoxComponent
            sx={{
              position: 'absolute',
              left: isLayoutOne ? getScaleSize(normalizePixels(51, 'width'), scale) : '0px',
              bottom: '0%',
            }}
          >
            <TypographyComponent
              sx={{ fontWeight: '500', fontSize: normalizePixels(12, 'height') }}
              variant="subtitle1"
            >
              {template.taxLabel.taxText}
            </TypographyComponent>
          </BoxComponent>
        )}
      </div>

      {isLayoutOne && (
        <div
          style={{
            marginTop: getScaleSize(normalizePixels(18, 'height'), scale),
            height: getScaleSize(normalizePixels(390, 'height'), scale),
          }}
        >
          <Media
            isDevelopment={development}
            media={media}
            height="100%"
            borderRadius="0px"
            playing={active}
            shouldLoad={shouldLoad}
          />
        </div>
      )}
    </div>
  );
});
